import {
  addHours,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { RangeTypes } from 'infrastructure/enums';
import { useMemo } from 'react';
import { getColorByLetters } from 'infrastructure/functions/get-color-by-letters';

import type { ISmsMetricData } from 'infrastructure/interfaces';
import type { IBarChartData } from 'components/atoms/charts/types';

interface IProps {
  rangeType: RangeTypes;
  data: ISmsMetricData[];
  bars: string[];
  colors?: Record<string, string>;
  startTime: number;
  endTime: number;
}

export const itemsMask = (props: IProps) => {
  const { data, rangeType, colors = {}, bars, startTime, endTime } = props;

  const items = useMemo<(IBarChartData & { hidden?: boolean })[]>(() => {
    let emptyData: IBarChartData[] = [];

    const today = new Date();

    if (rangeType === RangeTypes.Daily) {
      emptyData = Array.from({ length: 24 }).map((_, i) => {
        const hour = addHours(startOfDay(today), i);
        return {
          name: format(hour, 'h:mm a'),
          bars: bars.map((bar, index) => ({
            name: bar,
            index: index + 1,
            value: 0,
            color: colors[bar] ?? getColorByLetters(bar),
          })),
        };
      });
    }

    if (rangeType === RangeTypes.Weekly) {
      const days = eachDayOfInterval({
        start: startOfWeek(today),
        end: endOfWeek(today),
      });
      emptyData = days.map((day) => ({
        name: format(day, 'EEEE'),
        bars: bars.map((bar, index) => ({
          name: bar,
          index: index + 1,
          value: 0,
          color: colors[bar] ?? getColorByLetters(bar),
        })),
      }));
    }

    if (rangeType === RangeTypes.Monthly || rangeType === RangeTypes.Custom) {
      const days = eachDayOfInterval({
        start: startOfMonth(startTime!),
        end: endOfMonth(endTime!),
      });

      emptyData = days.map((day) => ({
        name: format(day, 'dd.MM'),
        bars: bars.map((bar, index) => ({
          name: bar,
          index: index + 1,
          value: 0,
          color: colors[bar] ?? getColorByLetters(bar),
        })),
      }));
    }

    const metrics = data ?? [];

    const filledData = emptyData.map((item) => {
      const match = metrics.find((el) => {
        const elTime = new Date(el.startTime);
        const itemTimeStr = item.name;

        if (rangeType === RangeTypes.Daily)
          return format(elTime, 'h:mm a') === itemTimeStr;
        if (rangeType === RangeTypes.Weekly)
          return format(elTime, 'EEEE') === itemTimeStr;
        if (rangeType === RangeTypes.Monthly || rangeType === RangeTypes.Custom)
          return format(elTime, 'dd.MM') === itemTimeStr;

        return false;
      });

      if (match) {
        return {
          name: item.name,
          bars: bars.map((bar, index) => ({
            name: bar,
            index: index + 1,
            value: match[bar] ? +match[bar] : 0,
            color: colors[bar] ?? getColorByLetters(bar),
          })),
        };
      }

      return item;
    });

    const adjustedData = filledData.map((item) => {
      if (rangeType === RangeTypes.Daily) {
        const hour = parseInt(
          format(parse(item.name, 'h:mm a', new Date()), 'H'),
          10,
        );
        if (hour % 2 === 1) {
          return { ...item, hidden: true };
        }
      }

      if (rangeType === RangeTypes.Monthly) {
        const day = parseInt(item.name.split('.')[0], 10);
        if (day % 2 === 0) {
          return { ...item, hidden: true };
        }
      }

      return item;
    });

    return adjustedData;
  }, [data, rangeType, bars, startTime, endTime]);

  return items;
};
